import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import './WildClubDateRoll.less';
import * as api from 'util/api';
import { StickyHeader } from 'components';
import STORES from 'config/stores';
import {
  Button,
  DatePicker,
  Table,
  Modal,
  Tag,
  Alert,
  Progress,
  Descriptions,
  Tooltip,
  Switch,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  EyeOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(tz);

const STORE = STORES.GPNZ;
const LOCAL_TIMEZONE = 'Pacific/Auckland';
const RECHARGE_TIMEZONE = 'EST';
const CHARGE_HOUR_EST = 8;

const ENABLE_TEST_MODE = false;
const DEFAULT_TEST_MODE = false;

const formatDate = (isoDate) => {
  if (!isoDate) return '—';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const formatShopifyFulfillmentStatus = (status) => {
  if (!status || status === 'UNKNOWN') return 'In Shopify';
  return status
    .split('_')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
};

const shopifyStatusTagColor = (status, onHold) => {
  if (onHold || status === 'ON_HOLD') return 'orange';
  if (status === 'FULFILLED') return 'green';
  if (status === 'PARTIALLY_FULFILLED' || status === 'IN_PROGRESS') return 'blue';
  if (status === 'UNKNOWN') return 'default';
  return 'default';
};

const HelpContent = () => (
  <div>
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 16 }}
      message="Shipment order dates — not charge dates"
      description="Rolls queued prepaid $0 Wild Club Recharge orders mapped to a Sanity release. Does not change subscription charge / membership dates. Does not release Shopify On Hold orders."
    />
    <p style={{ marginBottom: 8, fontWeight: 500 }}>How estimates are derived</p>
    <ul style={{ margin: '4px 0 16px', paddingLeft: 18 }}>
      <li>
        Counts use the same Box→release mapping as the member portal: each member&apos;s remaining
        boxes (queued, paid On Hold, or pending) consume the next Sanity release in calendar order.
      </li>
      <li style={{ marginTop: 6 }}>
        <Tag color="success" style={{ marginRight: 4 }}>Confirmed</Tag>
        {' '}
        and
        {' '}
        <Tag color="warning" style={{ marginRight: 4 }}>TBC</Tag>
        come live from Sanity. Fulfilled Shopify orders stop consuming releases.
      </li>
      <li style={{ marginTop: 6 }}>
        <strong>On Hold</strong>
        {' '}
        = Box 1 already in Shopify awaiting hold release on ship day (not rolled by this tool).
      </li>
      <li style={{ marginTop: 6 }}>
        <strong>Date TBC</strong>
        {' '}
        = members whose remaining boxes exceed the Sanity calendar — add more releases ahead.
      </li>
      <li style={{ marginTop: 6 }}>
        <strong>Exclude staff</strong>
        {' '}
        (header toggle, on by default) hides Shopify customers tagged
        {' '}
        <code>Staff</code>
        {' '}
        from timeline counts and member lists. The Roll action still updates every queued order,
        including staff.
      </li>
    </ul>
    <p style={{ marginBottom: 8, fontWeight: 500 }}>How to roll</p>
    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
      <li>
        Confirm the release date in Sanity first (
        <Tag color="success" style={{ marginRight: 4 }}>Confirmed</Tag>
        + planned dispatch date).
      </li>
      <li style={{ marginTop: 6 }}>
        Click
        {' '}
        <strong>Roll</strong>
        {' '}
        on that release row. Only the queued Recharge orders mapped to
        <em> that </em>
        release move — other boxes for the same member stay put.
      </li>
      <li style={{ marginTop: 6 }}>
        Target date defaults to the Sanity
        {' '}
        <code>plannedDispatchDate</code>
        . Adjust if you need lead time for pick/pack.
      </li>
    </ul>
  </div>
);

const renderStatusTag = (status) => {
  if (status === 'running') return <Tag icon={<SyncOutlined spin />} color="processing">Running</Tag>;
  if (status === 'completed') return <Tag icon={<CheckCircleOutlined />} color="success">Completed</Tag>;
  if (status === 'failed') return <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>;
  return <Tag>{status}</Tag>;
};

const buildCompletionDescription = (roll, onViewMissed) => (
  <span>
    {'Updated '}
    {roll.ordersUpdated}
    {' shipment orders, unchanged '}
    {roll.ordersSkipped}
    {', failed '}
    {roll.ordersFailed}
    {'. '}
    <Button type="link" onClick={onViewMissed} style={{ padding: 0 }}>
      View missed orders
    </Button>
  </span>
);

const getConvertedDates = (selectedDate) => {
  if (!selectedDate) return null;
  const userDateStr = selectedDate.format('YYYY-MM-DD');

  let est = null;
  const offsets = [0, -1, 1];
  for (let i = 0; i < offsets.length; i += 1) {
    const candidateStr = dayjs(userDateStr).add(offsets[i], 'day').format('YYYY-MM-DD');
    const candidate = dayjs.tz(candidateStr, RECHARGE_TIMEZONE)
      .hour(CHARGE_HOUR_EST).minute(0).second(0);
    if (candidate.tz(LOCAL_TIMEZONE).format('YYYY-MM-DD') === userDateStr) {
      est = candidate;
      break;
    }
  }

  if (!est) {
    est = dayjs.tz(userDateStr, RECHARGE_TIMEZONE)
      .hour(CHARGE_HOUR_EST).minute(0).second(0);
  }

  return {
    localDisplay: selectedDate.format('dddd D MMMM YYYY'),
    estDate: est.format('YYYY-MM-DD'),
    estDisplay: est.format('dddd D MMMM YYYY, h:mm A [EST]'),
    localShipTime: est.tz(LOCAL_TIMEZONE).format('dddd D MMMM YYYY, h:mm A'),
  };
};

const WildClubDateRoll = () => {
  const [testMode] = useState(DEFAULT_TEST_MODE);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeRoll, setActiveRoll] = useState(null);
  const [starting, setStarting] = useState(false);
  const [missedModalRollId, setMissedModalRollId] = useState(null);
  const [missedData, setMissedData] = useState(null);
  const [loadingMissed, setLoadingMissed] = useState(false);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [timelineModal, setTimelineModal] = useState(null);
  const [timelineLegendOpen, setTimelineLegendOpen] = useState(false);
  const [rollModalRelease, setRollModalRelease] = useState(null);
  const [rollTargetDate, setRollTargetDate] = useState(null);
  const [excludeStaff, setExcludeStaff] = useState(true);
  const pollRef = useRef(null);

  const fetchTimeline = useCallback(async (options = {}) => {
    setLoadingTimeline(true);
    try {
      const data = await api.getWildClubReleaseTimeline(STORE, {
        excludeStaff,
        ...options,
      });
      setTimeline(data);
    } catch (e) {
      // handled by api helper
    }
    setLoadingTimeline(false);
  }, [excludeStaff]);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await api.getWildClubDateRollHistory();
      setHistory(data);
      const running = data.find((r) => r.status === 'running');
      if (running) setActiveRoll(running);
    } catch (e) {
      // handled by api helper
    }
    setLoadingHistory(false);
  }, []);

  useEffect(() => {
    fetchTimeline();
    fetchHistory();
  }, [fetchTimeline, fetchHistory]);

  useEffect(() => {
    if (!activeRoll || activeRoll.status !== 'running') {
      if (pollRef.current) clearInterval(pollRef.current);
      return undefined;
    }

    const pollStatus = async () => {
      try {
        const data = await api.getWildClubDateRollStatus(activeRoll.key);
        setActiveRoll(data);
        if (data.status !== 'running') {
          if (pollRef.current) clearInterval(pollRef.current);
          fetchHistory();
          fetchTimeline();
        }
      } catch (e) {
        // keep polling
      }
    };

    pollStatus();
    pollRef.current = setInterval(pollStatus, 1500);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeRoll?.key, activeRoll?.status, fetchHistory, fetchTimeline]);

  const openRollModal = (release) => {
    setRollModalRelease(release);
    setRollTargetDate(
      release.plannedDispatchDate
        ? dayjs(release.plannedDispatchDate, 'YYYY-MM-DD')
        : null,
    );
  };

  const handleConfirmRoll = async () => {
    if (!rollModalRelease || !rollTargetDate) return;
    setStarting(true);
    try {
      const result = await api.startWildClubDateRoll(
        STORE,
        rollModalRelease.title,
        rollTargetDate.format('YYYY-MM-DD'),
        ENABLE_TEST_MODE ? testMode : false,
      );
      setActiveRoll({
        key: result.rollId,
        status: 'running',
        countryId: STORE,
        releaseTitle: result.releaseTitle,
        targetDateEst: result.targetDateEst,
        targetDateLocal: result.targetDateLocal,
        ordersTotal: 0,
        ordersUpdated: 0,
        ordersSkipped: 0,
        ordersFailed: 0,
      });
      setRollModalRelease(null);
      setRollTargetDate(null);
    } catch (e) {
      // handled by api helper
    }
    setStarting(false);
  };

  const handleViewMissed = async (rollId) => {
    setMissedModalRollId(rollId);
    setLoadingMissed(true);
    setMissedData(null);
    try {
      const data = await api.getWildClubDateRollMissed(rollId);
      setMissedData(data);
    } catch (e) {
      // handled by api helper
    }
    setLoadingMissed(false);
  };

  const exportMissedCsv = () => {
    if (!missedData?.orders?.length) return;
    const headers = [
      'Customer Name', 'Email', 'Order ID', 'Box', 'Scheduled (EST)', 'Scheduled (Local)', 'Recharge URL',
    ];
    const rows = missedData.orders.map((o) => [
      o.customerName, o.customerEmail, o.orderId, o.boxLabel,
      o.scheduledAtEst, o.scheduledAtLocal, o.rechargeUrl,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${(v || '').toString().replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `missed-wild-club-orders-roll-${missedModalRollId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const processed = activeRoll
    ? (activeRoll.ordersUpdated || 0)
      + (activeRoll.ordersSkipped || 0)
      + (activeRoll.ordersFailed || 0)
    : 0;
  const ordersTotal = activeRoll?.ordersTotal || 0;
  const progressPercent = ordersTotal
    ? Math.round((processed / ordersTotal) * 100)
    : 0;
  const showProgress = starting || activeRoll?.status === 'running';

  const historyColumns = [
    {
      title: 'Store',
      dataIndex: 'countryId',
      width: 80,
      render: (v) => (<Tag>{v}</Tag>),
    },
    {
      title: 'Release',
      dataIndex: 'releaseTitle',
      width: 180,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Target Date (EST)',
      dataIndex: 'targetDateEst',
      width: 140,
      render: (v) => formatDate(v),
    },
    {
      title: 'Target Date (NZ)',
      dataIndex: 'targetDateLocal',
      width: 140,
      render: (v) => formatDate(v),
    },
    {
      title: 'Rolled By',
      dataIndex: 'rolledByEmail',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Rolled At',
      dataIndex: 'rolledAt',
      width: 180,
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Updated',
      dataIndex: 'ordersUpdated',
      width: 80,
      render: (v) => (<Tag color="green">{v}</Tag>),
    },
    {
      title: 'Unchanged',
      dataIndex: 'ordersSkipped',
      width: 80,
      render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : <Tag>{v}</Tag>),
    },
    {
      title: 'Failed',
      dataIndex: 'ordersFailed',
      width: 80,
      render: (v) => (v > 0 ? <Tag color="red">{v}</Tag> : <Tag>{v}</Tag>),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (v) => renderStatusTag(v),
    },
    {
      title: 'Actions',
      width: 120,
      render: (_, record) => (record.status === 'completed' ? (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={(e) => { e.stopPropagation(); handleViewMissed(record.key); }}
        >
          Missed
        </Button>
      ) : null),
    },
  ];

  const missedColumns = [
    { title: 'Customer', dataIndex: 'customerName', width: 180 },
    {
      title: 'Email',
      dataIndex: 'customerEmail',
      width: 220,
      ellipsis: true,
    },
    { title: 'Order ID', dataIndex: 'orderId', width: 130 },
    { title: 'Box', dataIndex: 'boxLabel', width: 80 },
    {
      title: 'Scheduled (EST)',
      dataIndex: 'scheduledAtEst',
      width: 140,
      render: (v) => formatDate(v),
    },
    {
      title: 'Scheduled (NZ)',
      dataIndex: 'scheduledAtLocal',
      width: 150,
      render: (v) => formatDate(v),
    },
    {
      title: 'Recharge',
      dataIndex: 'rechargeUrl',
      width: 100,
      render: (url) => (url ? <a href={url} target="_blank" rel="noopener noreferrer">View</a> : '—'),
    },
  ];

  const isRolling = starting || activeRoll?.status === 'running';

  const timelineRows = (() => {
    if (!timeline) return [];
    const rows = [...(timeline.releases || [])];
    if (timeline.dateTbc?.estimatedOrders > 0) {
      rows.push({ ...timeline.dateTbc, isDateTbc: true });
    }
    return rows;
  })();

  const canRollRelease = (record) => (
    !record.isDateTbc
    && record.scheduleStatus === 'confirmed'
    && !!record.plannedDispatchDate
    && (record.queuedCount || 0) > 0
    && !isRolling
  );

  const convertedDates = getConvertedDates(rollTargetDate);

  return (
    <div id="wild-club-date-roll-container">
      <StickyHeader id="wild-club-date-roll-header">
        <span className="wild-club-header-store">Garage Project NZ</span>
        {showProgress && (
          <Tag icon={<SyncOutlined spin />} color="processing">
            Roll in progress
          </Tag>
        )}
        <Tooltip title="Hide members with the Shopify customer tag Staff from counts and lists. Roll still includes staff orders.">
          <span className="wild-club-staff-toggle">
            <Switch
              size="small"
              checked={excludeStaff}
              onChange={setExcludeStaff}
              disabled={showProgress || loadingTimeline}
            />
            <span>Exclude staff</span>
            {excludeStaff && timeline?.staffExcluded > 0 && (
              <Tag color="default">{`${timeline.staffExcluded} hidden`}</Tag>
            )}
          </span>
        </Tooltip>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => { fetchTimeline({ refresh: true }); fetchHistory(); }}
          loading={loadingTimeline || loadingHistory}
          disabled={showProgress}
        >
          Refresh
        </Button>
      </StickyHeader>

      <div className="date-roll-content">
        {showProgress && (
          <div className="date-roll-progress-section">
            <h3>
              <SyncOutlined spin />
              {starting && !activeRoll
                ? ' Starting shipment date roll…'
                : ` Roll in Progress — GPNZ${activeRoll?.releaseTitle ? ` — ${activeRoll.releaseTitle}` : ''}`}
            </h3>
            {activeRoll && (
              <p>
                {'Rolling shipment orders to EST '}
                {formatDate(activeRoll.targetDateEst)}
                {' (NZ '}
                {formatDate(activeRoll.targetDateLocal)}
                )
              </p>
            )}
            <Progress
              percent={ordersTotal ? progressPercent : 0}
              status="active"
              showInfo={!!ordersTotal}
            />
            <div className="date-roll-progress-stats">
              {ordersTotal > 0 ? (
                <>
                  <Tag color="blue">{`Processed: ${processed}/${ordersTotal}`}</Tag>
                  <Tag color="green">{`Updated: ${activeRoll.ordersUpdated || 0}`}</Tag>
                  <Tag color="orange">{`Unchanged: ${activeRoll.ordersSkipped || 0}`}</Tag>
                  <Tag color="red">{`Failed: ${activeRoll.ordersFailed || 0}`}</Tag>
                </>
              ) : (
                <Tag icon={<SyncOutlined spin />} color="processing">
                  Resolving queued orders for this release…
                </Tag>
              )}
            </div>
          </div>
        )}

        <div className="date-roll-summary-section">
          <div className="section-header-row">
            <h3>
              <UnorderedListOutlined />
              {' Release Timeline — estimated orders per Sanity release'}
            </h3>
            <Button
              icon={<InfoCircleOutlined />}
              size="small"
              onClick={() => setTimelineLegendOpen(true)}
            >
              Help me?
            </Button>
          </div>
          {loadingTimeline && <p>Loading release timeline...</p>}
          {timeline && !loadingTimeline && (
            <div>
              <p>
                {`${timeline.totalMembers} Wild Club member`}
                {timeline.totalMembers === 1 ? '' : 's'}
                {' · '}
                {timeline.releaseCount}
                {' upcoming release'}
                {timeline.releaseCount === 1 ? '' : 's'}
                {' in Sanity. Click '}
                <strong>Roll</strong>
                {' on a confirmed release to move its queued Recharge orders.'}
              </p>
              <Table
                dataSource={timelineRows}
                rowKey={(record) => (record.isDateTbc ? 'date-tbc' : record.title)}
                pagination={false}
                size="small"
                onRow={(record) => ({
                  onClick: () => setTimelineModal(record),
                  style: { cursor: 'pointer' },
                })}
                columns={[
                  {
                    title: 'Release',
                    dataIndex: 'title',
                    width: 180,
                    render: (v, record) => (record.isDateTbc ? 'Date TBC' : v),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'scheduleStatus',
                    width: 110,
                    render: (v) => (
                      v === 'confirmed'
                        ? <Tag color="success">Confirmed</Tag>
                        : <Tag color="warning">TBC</Tag>
                    ),
                  },
                  {
                    title: 'Date',
                    dataIndex: 'dateLabel',
                    width: 180,
                  },
                  {
                    title: 'Estimated orders',
                    dataIndex: 'estimatedOrders',
                    width: 130,
                    render: (v) => <Tag color="blue">{v}</Tag>,
                  },
                  {
                    title: 'On Hold',
                    dataIndex: 'onHoldCount',
                    width: 90,
                    render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : <Tag>0</Tag>),
                  },
                  {
                    title: 'Breakdown',
                    width: 220,
                    render: (_, record) => (
                      <span style={{ fontSize: 12, color: '#595959' }}>
                        {`${record.preparingCount || 0} preparing · ${record.queuedCount || 0} queued · ${record.pendingCount || 0} pending`}
                      </span>
                    ),
                  },
                  {
                    title: 'Actions',
                    width: 110,
                    render: (_, record) => {
                      if (record.isDateTbc) return null;
                      const enabled = canRollRelease(record);
                      let tip = 'Roll queued orders for this release';
                      if (record.scheduleStatus !== 'confirmed') {
                        tip = 'Confirm the release date in Sanity first';
                      } else if (!record.plannedDispatchDate) {
                        tip = 'Sanity release needs a plannedDispatchDate';
                      } else if (!(record.queuedCount > 0)) {
                        tip = 'No queued Recharge orders mapped to this release';
                      } else if (isRolling) {
                        tip = 'A roll is already running';
                      }
                      return (
                        <Tooltip title={tip}>
                          <Button
                            type="primary"
                            size="small"
                            icon={<CalendarOutlined />}
                            disabled={!enabled}
                            onClick={(e) => {
                              e.stopPropagation();
                              openRollModal(record);
                            }}
                          >
                            Roll
                          </Button>
                        </Tooltip>
                      );
                    },
                  },
                ]}
              />
            </div>
          )}
        </div>

        {activeRoll && activeRoll.status === 'completed' && (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
            message="Shipment date roll completed"
            description={buildCompletionDescription(
              activeRoll,
              () => handleViewMissed(activeRoll.key),
            )}
            closable
            onClose={() => setActiveRoll(null)}
          />
        )}

        {activeRoll && activeRoll.status === 'failed' && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
            message="Shipment date roll failed"
            description={activeRoll.errorMessage || 'An unexpected error occurred.'}
            closable
            onClose={() => setActiveRoll(null)}
          />
        )}

        <div className="date-roll-history-section">
          <h3>Roll History</h3>
          <Table
            dataSource={history}
            columns={historyColumns}
            rowKey="key"
            loading={loadingHistory}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1200 }}
            size="small"
            onRow={(record) => ({
              onClick: () => setHistoryDetail(record),
              style: { cursor: 'pointer' },
            })}
          />
        </div>
      </div>

      <Modal
        title={`Roll shipment dates — ${rollModalRelease?.title || ''}`}
        visible={rollModalRelease !== null}
        onCancel={() => { setRollModalRelease(null); setRollTargetDate(null); }}
        okText="Roll Shipment Dates"
        okType="primary"
        confirmLoading={starting}
        onOk={handleConfirmRoll}
        okButtonProps={{ disabled: !rollTargetDate }}
        width={560}
        destroyOnClose
      >
        {rollModalRelease && (
          <div>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Shipment order dates — not charge dates"
              description="Only queued prepaid Wild Club Recharge orders mapped to this release are updated. Subscription membership / next-charge dates are left alone. On Hold Shopify orders are out of scope."
            />
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Release">
                {rollModalRelease.title}
              </Descriptions.Item>
              <Descriptions.Item label="Queued (will roll)">
                <Tag color="orange">{rollModalRelease.queuedCount || 0}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="On Hold (excluded)">
                <Tag color="default">{rollModalRelease.onHoldCount || 0}</Tag>
                <span style={{ marginLeft: 8, fontSize: 12, color: '#8c8c8c' }}>
                  Release holds in Shopify on ship day
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Pending (investigate)">
                {(rollModalRelease.pendingCount || 0) > 0 ? (
                  <Tag color="red">{rollModalRelease.pendingCount}</Tag>
                ) : (
                  <Tag>0</Tag>
                )}
                {(rollModalRelease.pendingCount || 0) > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#cf1322' }}>
                    No Recharge order yet — cannot roll
                  </span>
                )}
              </Descriptions.Item>
            </Descriptions>

            <div className="date-roll-input-row">
              <span className="date-roll-label">Target date (NZ local time):</span>
              <DatePicker
                value={rollTargetDate}
                onChange={setRollTargetDate}
                format="DD/MM/YYYY"
                style={{ width: 200 }}
              />
            </div>
            <p style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
              Defaults to Sanity plannedDispatchDate. Adjust earlier if pick/pack needs lead time.
            </p>

            {convertedDates && (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Selected date (NZ)">
                  {convertedDates.localDisplay}
                </Descriptions.Item>
                <Descriptions.Item label="Recharge scheduled_at (EST)">
                  {convertedDates.estDisplay}
                </Descriptions.Item>
                <Descriptions.Item label="Local equivalent">
                  {convertedDates.localShipTime}
                </Descriptions.Item>
              </Descriptions>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={(
          <span>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            {`Missed Orders — Roll #${missedModalRollId}`}
          </span>
        )}
        visible={missedModalRollId !== null}
        onCancel={() => { setMissedModalRollId(null); setMissedData(null); }}
        footer={[
          <Button
            key="export"
            icon={<DownloadOutlined />}
            onClick={exportMissedCsv}
            disabled={!missedData?.orders?.length}
          >
            Export CSV
          </Button>,
          <Button
            key="close"
            type="primary"
            onClick={() => { setMissedModalRollId(null); setMissedData(null); }}
          >
            Close
          </Button>,
        ]}
        width={1100}
      >
        {missedData && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Showing ${missedData.total} queued order${missedData.total === 1 ? '' : 's'} still mapped to "${missedData.releaseTitle || 'this release'}" but not on target date ${formatDate(missedData.targetDateEst)} (EST)`}
            description="These orders were not rolled to the target date. Review manually in Recharge if they should have been included."
          />
        )}
        <Table
          dataSource={missedData?.orders || []}
          columns={missedColumns}
          rowKey="orderId"
          loading={loadingMissed}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </Modal>

      <Modal
        title={
          timelineModal?.isDateTbc
            ? `Date TBC — ${timelineModal?.estimatedOrders || 0} estimated orders`
            : `${timelineModal?.title || ''} — ${timelineModal?.estimatedOrders || 0} estimated orders`
        }
        visible={timelineModal !== null}
        onCancel={() => setTimelineModal(null)}
        footer={(
          <Button type="primary" onClick={() => setTimelineModal(null)}>
            Close
          </Button>
        )}
        width={1180}
      >
        {timelineModal && (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={timelineModal.dateLabel || 'Date TBC'}
              description={
                timelineModal.isDateTbc
                  ? 'These remaining boxes have no Sanity release left in the calendar. Add more tbc/confirmed releases ahead.'
                  : `${timelineModal.preparingCount || 0} preparing, ${timelineModal.queuedCount || 0} queued, ${timelineModal.pendingCount || 0} pending, ${timelineModal.onHoldCount || 0} On Hold in Shopify.`
              }
            />
            <Table
              dataSource={timelineModal.members || []}
              rowKey={(record) => `${record.subscriptionId}-${record.boxIndex}`}
              pagination={{ pageSize: 20 }}
              size="small"
              scroll={{ x: 1120 }}
              columns={[
                {
                  title: 'Customer',
                  dataIndex: 'customerName',
                  width: 160,
                  ellipsis: true,
                  render: (v, record) => (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {v || '—'}
                      {record.isStaff && !excludeStaff && (
                        <Tag color="purple">Staff</Tag>
                      )}
                    </span>
                  ),
                },
                {
                  title: 'Box',
                  dataIndex: 'boxOfTotal',
                  width: 60,
                  render: (v, record) => v || (record.boxIndex ? `${record.boxIndex}/3` : '—'),
                },
                {
                  title: 'Email',
                  dataIndex: 'customerEmail',
                  width: 200,
                  ellipsis: true,
                  render: (v) => v || '—',
                },
                {
                  title: 'Status',
                  dataIndex: 'statusLabel',
                  width: 120,
                  render: (v, record) => (
                    <>
                      <Tag>{v}</Tag>
                      {record.onHold ? <Tag color="orange">On Hold</Tag> : null}
                    </>
                  ),
                },
                {
                  title: 'Shopify',
                  width: 200,
                  render: (_, record) => {
                    if (!record.shopifyOrderId) return '—';
                    const label = formatShopifyFulfillmentStatus(
                      record.shopifyFulfillmentStatus,
                    );
                    const color = shopifyStatusTagColor(
                      record.shopifyFulfillmentStatus,
                      record.shopifyOnHold,
                    );
                    const linkText = record.shopifyOrderName || 'Open';
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Tag color={color}>{label}</Tag>
                        {record.shopifyUrl ? (
                          <a href={record.shopifyUrl} target="_blank" rel="noopener noreferrer">
                            {linkText}
                          </a>
                        ) : null}
                      </span>
                    );
                  },
                },
                {
                  title: 'Order date (Recharge)',
                  dataIndex: 'scheduledAt',
                  width: 140,
                  render: (v) => formatDate(v),
                },
                {
                  title: 'Recharge',
                  dataIndex: 'rechargeUrl',
                  width: 90,
                  render: (url) => (
                    url
                      ? <a href={url} target="_blank" rel="noopener noreferrer">Open</a>
                      : '—'
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>

      <Modal
        title="Help me?"
        visible={timelineLegendOpen}
        onCancel={() => setTimelineLegendOpen(false)}
        footer={(
          <Button type="primary" onClick={() => setTimelineLegendOpen(false)}>
            Close
          </Button>
        )}
        width={560}
      >
        <HelpContent />
      </Modal>

      <Modal
        title={`Roll #${historyDetail?.key || ''}`}
        visible={historyDetail !== null}
        onCancel={() => setHistoryDetail(null)}
        footer={(
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {historyDetail?.status === 'completed' && (
              <Button
                icon={<EyeOutlined />}
                onClick={() => {
                  handleViewMissed(historyDetail.key);
                  setHistoryDetail(null);
                }}
              >
                View missed orders
              </Button>
            )}
            <Button type="primary" onClick={() => setHistoryDetail(null)}>
              Close
            </Button>
          </div>
        )}
        width={480}
      >
        {historyDetail && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Store">
              <Tag>{historyDetail.countryId}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Release">
              {historyDetail.releaseTitle || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Target Date (EST)">
              {formatDate(historyDetail.targetDateEst)}
            </Descriptions.Item>
            <Descriptions.Item label="Target Date (NZ)">
              {formatDate(historyDetail.targetDateLocal)}
            </Descriptions.Item>
            <Descriptions.Item label="Rolled By">
              {historyDetail.rolledByEmail || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Rolled At">
              {historyDetail.rolledAt
                ? dayjs(historyDetail.rolledAt).format('DD/MM/YYYY HH:mm')
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {renderStatusTag(historyDetail.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Orders Updated">
              <Tag color="green">{historyDetail.ordersUpdated}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Orders Unchanged">
              <Tag color={historyDetail.ordersSkipped > 0 ? 'orange' : 'default'}>
                {historyDetail.ordersSkipped}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Orders Failed">
              <Tag color={historyDetail.ordersFailed > 0 ? 'red' : 'default'}>
                {historyDetail.ordersFailed}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default WildClubDateRoll;
